import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { 
  LockClosedIcon, 
  UserIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ShieldExclamationIcon,
  XMarkIcon,
  ArrowRightIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';

const Login = () => {
  const [formData, setFormData] = useState({ usernameOrEmail: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rateLimitError, setRateLimitError] = useState(null);
  const [retryAfter, setRetryAfter] = useState(0);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let interval;
    if (rateLimitError && retryAfter > 0) {
      interval = setInterval(() => {
        setRetryAfter(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setRateLimitError(null);
            setFailedAttempts(0);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => interval && clearInterval(interval);
  }, [rateLimitError, retryAfter]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.usernameOrEmail || !formData.password) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      const response = await authAPI.login(formData.usernameOrEmail.trim(), formData.password);
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('role', response.data.role);
        window.location.href = '/';
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setRetryAfter(60);
        setRateLimitError({ message: 'Too many login attempts' });
      } else {
        setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
        setFailedAttempts(prev => prev + 1);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem',
      background: '#0a0a0a',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Background - Cyan Accents */}
      <div style={{
        position: 'absolute',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(0, 212, 255, 0.15), transparent)',
        borderRadius: '50%',
        top: '-250px',
        right: '-250px',
        animation: 'pulse 3s ease-in-out infinite'
      }} />
      <div style={{
        position: 'absolute',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(0, 212, 255, 0.08), transparent)',
        borderRadius: '50%',
        bottom: '-300px',
        left: '-300px',
        animation: 'pulse 4s ease-in-out infinite reverse'
      }} />

      {/* Login Card - Dark Theme */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        width: '100%',
        maxWidth: '440px',
        background: '#1a1a2e',
        backdropFilter: 'blur(20px)',
        borderRadius: '2rem',
        border: '1px solid rgba(0, 212, 255, 0.4)',
        boxShadow: '0 25px 50px -12px rgba(0, 212, 255, 0.2)',
        padding: '2rem'
      }}>
        
        {/* Header - Updated to Cyan */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '70px',
            height: '70px',
            background: '#00d4ff',
            borderRadius: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.25rem',
            boxShadow: '0 10px 25px -5px rgba(0, 212, 255, 0.4)'
          }}>
            <LockClosedIcon style={{ width: '2rem', height: '2rem', color: '#1a1a2e' }} />
          </div>
          <h1 style={{ 
            fontSize: '1.875rem', 
            fontWeight: 'bold', 
            background: 'linear-gradient(135deg, #fff, #00d4ff)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem' 
          }}>Welcome Back</h1>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>Sign in to your account</p>
        </div>

        {/* Rate Limit Error */}
        {rateLimitError && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '1rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <ShieldExclamationIcon style={{ width: '1.25rem', height: '1.25rem', color: '#f87171' }} />
                <h3 style={{ fontWeight: '600', color: '#fecaca', fontSize: '0.875rem' }}>Login Temporarily Disabled</h3>
              </div>
              <button onClick={() => setRateLimitError(null)} style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem' }}>
                <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
              </button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'rgba(0, 0, 0, 0.3)', borderRadius: '0.75rem', padding: '0.75rem' }}>
              <ClockIcon style={{ width: '1.25rem', height: '1.25rem', color: '#f87171' }} />
              <div>
                <p style={{ fontSize: '0.75rem', color: '#fecaca' }}>You can try again in:</p>
                <p style={{ fontFamily: 'monospace', fontSize: '1.25rem', fontWeight: 'bold', color: 'white' }}>
                  {Math.floor(retryAfter / 60)}:{String(retryAfter % 60).padStart(2, '0')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && !rateLimitError && (
          <div style={{
            marginBottom: '1.5rem',
            padding: '0.875rem 1rem',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <ExclamationTriangleIcon style={{ width: '1rem', height: '1rem', color: '#f87171', flexShrink: 0 }} />
            <span style={{ fontSize: '0.875rem', color: '#fecaca', flex: 1 }}>{error}</span>
            <button onClick={() => setError('')} style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer' }}>
              <XMarkIcon style={{ width: '1rem', height: '1rem' }} />
            </button>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Username/Email Input */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              <UserIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              Username or Email
            </label>
            <input
              type="text"
              name="usernameOrEmail"
              value={formData.usernameOrEmail}
              onChange={(e) => setFormData({ ...formData, usernameOrEmail: e.target.value })}
              placeholder="Enter username or email"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                background: '#0f0f0f',
                border: '1px solid rgba(0, 212, 255, 0.3)',
                borderRadius: '1rem',
                color: 'white',
                fontSize: '0.875rem',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
              onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
              required
              disabled={loading || rateLimitError}
            />
          </div>

          {/* Password Input with Eye Toggle */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: '#e2e8f0', marginBottom: '0.5rem' }}>
              <LockClosedIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff' }} />
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '0.875rem 3rem 0.875rem 1rem',
                  background: '#0f0f0f',
                  border: '1px solid rgba(0, 212, 255, 0.3)',
                  borderRadius: '1rem',
                  color: 'white',
                  fontSize: '0.875rem',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#00d4ff'}
                onBlur={(e) => e.target.style.borderColor = 'rgba(0, 212, 255, 0.3)'}
                required
                disabled={loading || rateLimitError}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#94a3b8',
                  transition: 'color 0.2s'
                }}
                onMouseEnter={(e) => e.target.style.color = '#00d4ff'}
                onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
              >
                {showPassword ? (
                  <EyeSlashIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                ) : (
                  <EyeIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                )}
              </button>
            </div>
          </div>

          {/* Options */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input type="checkbox" style={{ width: '1rem', height: '1rem', accentColor: '#00d4ff' }} disabled={rateLimitError} />
              <span style={{ fontSize: '0.875rem', color: '#94a3b8' }}>Remember me</span>
            </label>
            <Link to="/forgot-password" style={{ fontSize: '0.875rem', color: '#00d4ff', textDecoration: 'none', transition: 'color 0.2s' }}>
              Forgot password?
            </Link>
          </div>

          {/* Submit Button - Updated to Cyan */}
          <button
            type="submit"
            disabled={loading || rateLimitError}
            style={{
              width: '100%',
              padding: '0.875rem',
              borderRadius: '1rem',
              fontWeight: '600',
              color: loading || rateLimitError ? '#94a3b8' : '#1a1a2e',
              border: 'none',
              cursor: loading || rateLimitError ? 'not-allowed' : 'pointer',
              background: loading || rateLimitError ? '#334155' : '#00d4ff',
              transition: 'all 0.2s',
              fontSize: '0.875rem'
            }}
            onMouseEnter={(e) => {
              if (!loading && !rateLimitError) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 10px 25px -5px rgba(0, 212, 255, 0.4)';
                e.target.style.background = '#00e5ff';
              }
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = 'none';
              if (!loading && !rateLimitError) {
                e.target.style.background = '#00d4ff';
              }
            }}
          >
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <div style={{ width: '1.25rem', height: '1.25rem', border: '2px solid #1a1a2e', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                <span>Signing in...</span>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                <span>Sign In</span>
                <ArrowRightIcon style={{ width: '1rem', height: '1rem' }} />
              </div>
            )}
          </button>
        </form>

        {/* Footer */}
        <div style={{ marginTop: '1.75rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(0, 212, 255, 0.2)', textAlign: 'center' }}>
          <p style={{ fontSize: '0.875rem', color: '#94a3b8' }}>
            Don't have an account?{' '}
            <Link to="/register" style={{ color: '#00d4ff', fontWeight: '600', textDecoration: 'none', transition: 'color 0.2s' }}>
              Sign up
            </Link>
          </p>
          {failedAttempts > 0 && !rateLimitError && (
            <div style={{ marginTop: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.75rem', color: '#fbbf24' }}>
              <ExclamationTriangleIcon style={{ width: '0.75rem', height: '0.75rem' }} />
              <span>{failedAttempts} failed attempt{failedAttempts !== 1 ? 's' : ''}</span>
            </div>
          )}
        </div>

        {/* Security Notice */}
        {failedAttempts >= 2 && (
          <div style={{
            marginTop: '1rem',
            padding: '0.875rem',
            background: 'rgba(0, 212, 255, 0.1)',
            border: '1px solid rgba(0, 212, 255, 0.3)',
            borderRadius: '1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
              <ShieldExclamationIcon style={{ width: '1rem', height: '1rem', color: '#00d4ff', flexShrink: 0, marginTop: '0.125rem' }} />
              <div>
                <h4 style={{ fontSize: '0.75rem', fontWeight: '600', color: '#00d4ff' }}>Security Notice</h4>
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>
                  Multiple failed login attempts detected. Your account may be locked after 5 attempts.
                </p>
                <Link to="/forgot-password" style={{ fontSize: '0.75rem', color: '#00d4ff', textDecoration: 'none', marginTop: '0.5rem', display: 'inline-block' }}>
                  Reset your password →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default Login;